const amountLocale = (language: string | undefined): string => {
  const lang = (language ?? "en").split("-")[0];
  if (lang === "es") return "es-MX";
  if (lang === "fr") return "fr-FR";
  return "en-US";
};

/** Same shape as `formatLabeledAmount`: decimal plus ISO code, `$` only for USD. */
const formatShareAmount = (
  amount: number,
  currency: string,
  locale: string
): string => {
  const formatted = new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
};

/**
 * First usable name among candidates, in priority order. The backend
 * create-time "Unknown" placeholder is not a name.
 */
export function pickShareFirstName(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed && trimmed.toLowerCase() !== "unknown") return trimmed;
  }
  return undefined;
}

type ShareTranslate = (
  key: string,
  options?: Record<string, string>
) => string;

export function buildShareText(
  t: ShareTranslate,
  input: {
    firstName?: string | null;
    amount?: number | null;
    currency?: string | null;
    url: string;
    language?: string;
  }
): string {
  const firstName = input.firstName?.trim() ?? "";
  const amount =
    input.amount != null &&
    Number.isFinite(input.amount) &&
    input.currency?.trim()
      ? formatShareAmount(
          input.amount,
          input.currency,
          amountLocale(input.language)
        )
      : "";

  return t(
    firstName ? "share.shareTextWithName" : "share.shareTextWithoutName",
    { firstName, amount, url: input.url }
  );
}

const isShareAbort = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  "name" in err &&
  (err as { name?: string }).name === "AbortError";

/**
 * Shares the full message as `text` only, so targets do not append the URL twice.
 * Cancel stops. A missing Web Share API copies the message. Other share
 * failures do not copy.
 */
export async function shareOrCopyMessage(
  message: string,
  options?: { title?: string; fallbackPrompt?: string }
): Promise<"shared" | "copied" | "cancelled" | "failed"> {
  const text = message.trim();
  if (!text) return "failed";

  if (typeof navigator.share === "function") {
    try {
      await navigator.share({
        ...(options?.title ? { title: options.title } : {}),
        text,
      });
      return "shared";
    } catch (err) {
      if (isShareAbort(err)) return "cancelled";
      return "failed";
    }
  }

  try {
    await navigator.clipboard.writeText(text);
    return "copied";
  } catch {
    if (options?.fallbackPrompt) {
      window.prompt(options.fallbackPrompt, text);
    }
    return "failed";
  }
}
